import { languages } from '@shared/library/dist/static/language';
import moment from 'moment';
import React from 'react';
import config from '../../../../config';
import './AdministrationPanel.scss';
import {
    create,
    edit,
    get
} from './static/methods';
//#region ICONS
import { ReactComponent as ToolIcon } from "./icons/Tool.svg";
import { ReactComponent as UtentiWebIcon } from "./icons/UtentiWeb.svg";
//#endregion
//#region MODELS
import { GridColDef, GridRenderEditCellParams, GridRowId, GridValidRowModel } from '@mui/x-data-grid';
import { dateColumnSettings, formDataToObject, GestCard, GridColDefPro, iconColumnSettings, IGestDataGridProProps, ISharedConfig, setDialog, setSnackbar, Title } from '@shared/library';
import { IGestSelectOption, ILabeledID } from '@shared/library/dist/components/gest/GestInputs/select/GestSelectProps';
import { AxiosError } from 'axios';
import { LangKey } from '../../../../models/ui_data/Lang_model';
import {
    IDataGridColumn,
    IGridContent,
    ISummary,
    ITabTableConfig
} from './models/components/components';
import { Hardware } from './models/entities/Hardware';
import { ILicense } from './models/entities/License';
import { TAllowedCreateEntities } from './models/entities/Shared';
import { IPermission, ISoftwarePermission, IUser } from './models/entities/User';
//#endregion
//#region COMPONENTS
import { Tooltip } from "@shared/library";
import GestButton from '@shared/library/dist/components/gest/GestInputs/button/GestButton';
import GestSelect from '@shared/library/dist/components/gest/GestInputs/select/GestSelect';
import GestSwitch from '@shared/library/dist/components/gest/GestInputs/switch/GestSwitch';
import DataGridsLayout from './components/DataGridsLayout/DataGridsLayout';
import UserCreateForm from './components/UserCreateForm/UserCreateForm';
//#endregion

export type TSection = IUser | ISoftwarePermission | Hardware | ILicense;

const AdministrationPanel = (props: ISharedConfig) => {
    //#region LOGIC

    //#region STATIC
    const lang = languages(props.settings?.lang);
    document.title = config.app.name;
    //#endregion

    //#region STATE
    const form = React.useRef(null);
    const [clickedArea, setClickedArea] = React.useState<ISummary<TSection>>();
    //USERS
    const [usersTabs, setUsersTabs] = React.useState<IGridContent<IUser>>();
    const [editedField, setEditedField] = React.useState<string>();
    //SOFTWARE PERMISSIONS
    const [permissionsColumns, setPermissionsColumns] = React.useState<GridColDefPro<ISoftwarePermission>[]>([]);
    //USER CREATE MODAL
    const roles = React.useRef<IGestSelectOption<undefined>[]>([]);
    //LICENSE
    const [licenseTabs, setLicenseTabs] = React.useState<IGridContent<ILicense>>();
    //HARDWARE
    const [hardwareTabs, setHardwareTabs] = React.useState<IGridContent<Hardware>>();
    //FLAGS
    const [loadingUsers, setLoadingUsers] = React.useState(false);
    const [loadingPermissions, setLoadingPermissions] = React.useState(false);
    const [loadingCreate, setLoadingCreate] = React.useState(false);
    const [loadingLicenses, setLoadingLicenses] = React.useState(false);
    const [loadingHardwares, setLoadingHardwares] = React.useState(false);
    //#endregion

    //#region METHODS

    //#region API CALLS
    //#region GET
    const initUsers = () => {
        setLoadingUsers(true);
        return get<IGridContent<IUser>>("Users")
            .then(res => {
                initPermissions(res);

                const userColumns = res.Columns?.filter(c => !c.icon && !(c.name || c.field).toLowerCase().includes("_"));
                const columns = setupColumns(userColumns).map<GridColDef<IUser, string | boolean>>(c => (c.name || c.field).toLowerCase() === "status"
                    ? iconColumnSettings(
                        "Status",
                        params =>
                            <GestSwitch
                                checked={params.value as boolean}
                                onchange={checked => editUserField("User", params.id, params.field, checked)}
                            />,
                        params => `${params.value ? "Disabilita" : "Abilita"} Accesso Web`
                    )
                    : {
                        ...c,
                        editable: true,
                        renderEditCell: c.SelectOptions?.length
                            ? params =>
                                <GestSelect
                                    fullw
                                    value={params.value?.toString()}
                                    options={c.SelectOptions!.map(o => ({
                                        value: (o as ILabeledID).ID,
                                        label: lang[(o as ILabeledID).ID?.toString().toLowerCase() as LangKey]
                                    }))}
                                    onchange={selectedOpts => updateCustomEditing(selectedOpts[0]?.value?.toString(), params)}
                                />
                            : undefined
                        ,
                    }
                );
                const userSection: IGridContent<IUser> = {
                    Tabs: [{ InitRows: res.InitRows! }],
                    Columns: columns,
                    actions: <GestButton label="crea nuovo utente" onclick={openCreateUserForm} />,
                };

                setUsersTabs(userSection);
                setClickedArea({ label: "utenti", content: userSection } as ISummary<TSection>);
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: "Impossibile recuperare utenti e relativi permessi web", severity: "error" }))
            .finally(() => setLoadingUsers(false));
    };

    const initLicenses = () => {
        setLoadingLicenses(true);
        return get<IGridContent<ILicense>>("Licenses")
            .then(res => {
                const columns = setupColumns(res.Columns).map<GridColDef<ILicense>>(c =>
                    (c.name || c.field).toLowerCase().includes("valid")
                        ? dateColumnSettings(c.name || c.field, lang)
                        : {
                            ...c,
                            valueGetter: (value, row) => {
                                const opt = (c.SelectOptions as ILabeledID[])?.find(o => o.ID === (row as ILabeledID).ID)?.Label;
                                return lang[(opt ?? value) as LangKey] ?? opt ?? value;
                            },
                        }
                );
                setLicenseTabs({ Columns: columns, Tabs: res.Tabs });
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: "Impossibile recuperare licenze", severity: "error" }))
            .finally(() => setLoadingLicenses(false));
    };

    const initHardwares = () => {
        setLoadingHardwares(true);
        return get<IGridContent<Hardware>>("Hardwares")
            .then(res => {
                const columns = setupColumns(res.Columns).map<GridColDef<Hardware>>(c =>
                    (c.name || c.field).toLowerCase().includes("date")
                        ? dateColumnSettings(c.name || c.field, lang)
                        : {
                            ...c,
                            valueGetter: (value, row) => {
                                const opt = (c.SelectOptions as ILabeledID[])?.find(o => o.ID === (row as ILabeledID).ID)?.Label;
                                return lang[(opt ?? value) as LangKey] ?? opt ?? value;
                            },
                        }
                );
                setHardwareTabs({ Columns: columns, Tabs: res.Tabs });
            })
            .catch((err) => setSnackbar(props, { message: "Impossibile recuperare hardwares", severity: "error" }))
            .finally(() => setLoadingHardwares(false));
    };
    //#endregion

    //PATCH
    const editUserField = <T extends IUser | IPermission>(path: string, ID: number | string | bigint, field: string, newValue?: boolean | string | number | bigint) => {
        setLoadingUsers(true);
        return edit<T>(ID, newValue, path, field)
            .then(updatedRow => {
                if (updatedRow) {
                    setSnackbar(props, { message: "Campo modificato con successo", severity: "success" });
                }
                else throw "";
            })
            .catch((err: AxiosError) => setSnackbar(props, { message: "Impossibile modificare questo campo", severity: "error" }))
            .finally(() => setLoadingUsers(false));
    };

    //POST
    const createUser = (evt: React.SubmitEvent<HTMLFormElement>) => {
        evt.preventDefault();

        if (form.current) {
            const newItem = formDataToObject<TAllowedCreateEntities>(form.current);

            if (newItem) {
                setLoadingCreate(true);
                return create<IUser[]>("User", newItem)
                    .then(InitRows => {
                        const userSection: IGridContent<IUser> = {
                            ...usersTabs,
                            Tabs: [{ InitRows }],
                            actions: <GestButton label="crea nuovo utente" onclick={openCreateUserForm} />
                        };
                        setUsersTabs(userSection);
                        setClickedArea({ label: "utenti", content: userSection, } as ISummary<TSection>);

                        setSnackbar(props, { message: "Creazione utente avvenuta con successo", severity: "success" });
                        setDialog(props);
                    })
                    .catch((err) => setSnackbar(props, { message: "Errore durante la creazione", severity: "error" }))
                    .finally(() => setLoadingCreate(false));
            }
        }
    };
    //#endregion

    const setupColumns = <T extends GridValidRowModel>(columns?: IDataGridColumn<T>[]) => columns?.map<IDataGridColumn<T>>(c => ({
        ...c,
        headerName: (c.name || c.field)?.includes(";")
            ? `${lang?.[(c.name || c.field).split(";")[0] as LangKey]} (${lang?.[(c.name || c.field).split(";")[1] as LangKey]})`
            : lang?.[(c.name || c.field) as LangKey],
        flex: 1,
        maxWidth: 450,
        cellClassName: params => (params.value?.Role || ((c.name || c.field).toLowerCase() === "status" ? params.row.Status : params.value))?.toString().toLowerCase(),
        renderCell: params => lang[(params.value?.Role || params.value) as LangKey] || params.value?.Label,
    })) ?? [];

    const updateCustomEditing = <TRow extends GridValidRowModel, TValue>(value: TValue, params: GridRenderEditCellParams<TRow, TValue>) => {
        const { id, field } = params;

        params.api.setEditCellValue({ id, field, value });
        params.api.stopCellEditMode({ id, field });
    };

    const submitCustomEditing = async <TRow,>(curr: TRow, prev: TRow, params: { rowId: GridRowId; }) => {
        const _ = require('lodash');
        let isUpdated = !_.isEqual(curr, prev);
        let updated = structuredClone(curr);

        if (isUpdated && editedField) {
            let path = "User";
            let field = editedField;
            let newValue: string | number | bigint = (curr as any)[field];
            let ID = params.rowId;

            switch (clickedArea?.label) {
                case "permessi software": {
                    path += `/${params.rowId}/Permission`;
                    field = "Role";

                    const entity = curr as ISoftwarePermission;
                    const value = entity[editedField] as IPermission;
                    newValue = value.Role;
                    ID = value.SoftwareID || setupColumns(permissionsColumns).find(pc => pc.field === editedField)!.ID as GridRowId;

                    updated = { ...curr, [editedField]: value };
                    break;
                }

                case "licenze": {
                    path = "License";
                    field = "Terminal";

                    const entity = curr as ILicense;
                    const value = (entity.Terminal as IGestSelectOption<undefined>);
                    newValue = value.value!;

                    updated = { ...curr, [editedField]: { ID: newValue, Label: value.label } };
                    break;
                }

                case undefined: return;
                default: break;
            }

            isUpdated = Boolean(await editUserField(path, ID, field, newValue));
        }

        return isUpdated ? updated : prev;
    };

    const initPermissions = async (res: IGridContent<IUser>) => await new Promise<void>(() => {
        roles.current = res.Columns
            ?.find(c => c.field.toLowerCase() === "role")?.SelectOptions
            ?.map(o => ({
                value: (o as ILabeledID).ID,
                label: lang[(o as ILabeledID).ID?.toString().toLowerCase() as LangKey]
            })) ?? [];

        const permissionColumns = res.Columns?.filter(c => (c.name || c.field).toLowerCase().includes("username") || c.icon || (c.name || c.field).toLowerCase().includes("_")) as GridColDef<ISoftwarePermission>[];
        const softwarePermissionCols = setupColumns(permissionColumns).map<GridColDef<ISoftwarePermission, IPermission | string | number>>(c => ({
            ...c,
            editable: !(c.name || c.field).toLowerCase().includes("username"),
            minWidth: 150,
            renderEditCell: !c.field.toLowerCase().includes("username")
                ? params =>
                    <GestSelect
                        fullw
                        required
                        value={(params.value as IPermission)?.Role}
                        options={roles.current}
                        onchange={selectedOpts => updateCustomEditing({ ...(params.value as IPermission), Role: selectedOpts[0]?.value!.toString() }, params)}
                    />
                : undefined
            ,
        }));

        setPermissionsColumns(softwarePermissionCols);
    });

    //MODALS
    const openCreateUserForm = () => setDialog(props, {
        label: "crea nuovo utente",
        content:
            <form ref={form} onSubmit={createUser} style={{ width: 400 }}>
                <UserCreateForm roles={roles.current} />
                <GestButton fullw submit label="crea" h={40} loading={loadingCreate} />
            </form>
    });
    //#endregion

    //STATE CHANGE
    React.useEffect(() => {
        initUsers();
        initLicenses();
        initHardwares();
    }, []);

    return props.session?.userData?.role !== "admin" ? <h2>Accesso negato a questa area</h2> : (
        <div id="AdministrationPanel" className='flex-column'>
            <Tooltip arrow placement='top' title={"Attivi / Disponibili"}>
                <nav id="summary" className='flex fullw'>
                    <GestCard id='users'
                        class={`fullw ${clickedArea?.label === "utenti" ? "active" : ""}`}
                        title={"Utenti"}
                        onClick={() => setClickedArea({
                            label: "utenti",
                            content: {
                                ...usersTabs,
                                actions: <GestButton label="crea nuovo utente" onclick={openCreateUserForm} />,
                                Columns: setupColumns(usersTabs?.Columns).map(c => (c.name || c.field).toLowerCase() === "status"
                                    ? iconColumnSettings<IUser, boolean>(
                                        "Status",
                                        params =>
                                            <GestSwitch
                                                checked={params.value}
                                                onchange={checked => editUserField("User", params.id, params.field, checked)}
                                            />,
                                        params => `${params.value ? "Disabilita" : "Abilita"} Accesso Web`
                                    )
                                    : c) as IDataGridColumn<TSection>[],
                            }
                        })}
                    >
                        <div className='icon centered'><UtentiWebIcon /></div>
                        <Title size='lg' class="align-items-center">{usersTabs?.Tabs?.[0].InitRows.filter(u => u.Status).length ?? 0} / {usersTabs?.Tabs?.[0].InitRows.length ?? 0}</Title>
                    </GestCard>

                    <GestCard id='permissions'
                        class={`fullw ${clickedArea?.label === "permessi software" ? "active" : ""}`}
                        title={"Permessi Software"}
                        onClick={() => setClickedArea({
                            label: "permessi software",
                            content: {
                                Columns: setupColumns(permissionsColumns) as IDataGridColumn<TSection>[],
                                Tabs: usersTabs?.Tabs?.map<ITabTableConfig<ISoftwarePermission>>(t => ({ ...t, InitRows: t.InitRows.filter(u => u.Permissions).map(u => ({ ...u.Permissions!, id: u.ID, Username: u.Username } as ISoftwarePermission)) }))
                            }
                        })}
                    >
                        <div className='icon centered'><ToolIcon /></div>

                        <Title size='lg' class="align-items-center">{usersTabs?.Tabs?.[0]?.InitRows
                            .filter(u => u.Permissions)
                            .flatMap(u => Object.keys(u.Permissions!).filter(p => (u.Permissions as any)[p] !== "disabled").length)
                            .reduce((prev, curr) => prev += curr, 0)} / {permissionsColumns.length * (usersTabs?.Tabs?.[0].InitRows.length ?? 0)}</Title>
                    </GestCard>

                    <GestCard id="licenses"
                        class={`fullw ${clickedArea?.label === "licenze" ? "active" : ""}`}
                        title={"Licenze"}
                        onClick={() => setClickedArea({ label: "licenze", content: { ...licenseTabs, Columns: setupColumns(licenseTabs?.Columns) as IDataGridColumn<TSection>[] } })}
                    >
                        {licenseTabs?.Tabs?.map(t =>
                            <>
                                <div className='icon centered'>{<img src={require(`./icons/${t.TabName?.toLowerCase()}.svg`)} alt={`icon-${t.TabName}`} />}</div>

                                <div className='fullw text-capitalize'>
                                    <span className='text-s'>{t.TabName}</span>
                                    <Title size='lg' class="align-items-center">{t.InitRows.filter(l => !moment(new Date()).isAfter(l.ValidTo)).length} / {t.InitRows.length ?? 0}</Title>
                                </div>
                            </>
                        )}
                    </GestCard>

                    <GestCard
                        id="hardwares"
                        class={`fullw ${clickedArea?.label === "hardwares" ? "active" : ""}`}
                        title={"Hardwares"}
                        onClick={() => setClickedArea({ label: "hardwares", content: { ...hardwareTabs, Columns: setupColumns(hardwareTabs?.Columns) as IDataGridColumn<TSection>[] } })}
                    >

                        {hardwareTabs?.Tabs?.map(t =>
                            <>
                                <div className='icon centered'>{<img src={require(`./icons/${t.TabName?.toLowerCase()}.svg`)} alt={`icon-${t.TabName}`} />}</div>

                                <div className='fullw text-capitalize'>
                                    <span className='text-s'>{t.TabName}</span>
                                    <Title size='lg' class="align-items-center">{t.InitRows.filter(h => h.Status !== "replaced" && h.Status !== "returned").length} / {t.InitRows.length ?? 0}</Title>
                                </div>
                            </>
                        )}
                    </GestCard>
                </nav>
            </Tooltip>

            <DataGridsLayout
                area={clickedArea}
                gridProps={{
                    columns: [],
                    onCellEditStart: params => setEditedField(params.field),
                    processRowUpdate: submitCustomEditing,
                    getRowClassName: params => clickedArea?.label === "licenze" && moment(new Date()).isAfter((params.row as ILicense).ValidTo)
                        ? "draftRemove"
                        : "",
                    loading:
                        (clickedArea?.label === "hardwares" && loadingHardwares) ||
                        (clickedArea?.label === "licenze" && loadingLicenses) ||
                        loadingUsers,
                } as IGestDataGridProProps<TSection>}
            />
        </div>
    );
};
export default React.memo(AdministrationPanel);